import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseIncrementalRenderOptions {
  initial?: number;
  step?: number;
  /** Prefetch margin around the viewport so the cap grows BEFORE the sentinel
   * is scrolled fully into view (smoother, and avoids exact-edge misses). */
  rootMargin?: string;
}

export interface UseIncrementalRenderResult<T> {
  visible: T[];
  sentinelRef: (el: HTMLElement | null) => void;
  hasMore: boolean;
  shownCount: number;
  total: number;
}

/**
 * Incrementally renders a (potentially huge) list to keep the DOM small.
 *
 * Filtering / sorting must happen over the FULL array by the caller; this hook
 * only caps how many items are RENDERED and grows the cap as the sentinel
 * scrolls into view. Every item stays reachable by scrolling.
 *
 * The shown cap resets to `initial` whenever the `items` array identity changes
 * or its length changes — so a fresh search jumps the list back to the top.
 */
export function useIncrementalRender<T>(
  items: T[],
  opts?: UseIncrementalRenderOptions
): UseIncrementalRenderResult<T> {
  const initial = opts?.initial ?? 60;
  const step = opts?.step ?? 60;
  const rootMargin = opts?.rootMargin ?? '600px 0px';

  const [shown, setShown] = useState(initial);

  // Reset to the top when the list changes (new search / filter / sort).
  const prevRef = useRef<{ items: T[]; length: number }>({
    items,
    length: items.length,
  });
  if (
    prevRef.current.items !== items ||
    prevRef.current.length !== items.length
  ) {
    prevRef.current = { items, length: items.length };
    if (shown !== initial) setShown(initial);
  }

  const hasMore = shown < items.length;

  // Keep the latest values available to the observer callback without
  // recreating the observer on every render.
  const stateRef = useRef({ shown, hasMore, length: items.length, step });
  stateRef.current = { shown, hasMore, length: items.length, step };

  const observerRef = useRef<IntersectionObserver | null>(null);

  const sentinelRef = useCallback(
    (el: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!el) return;
      if (typeof IntersectionObserver === 'undefined') return; // SSR-safe: render all
      const observer = new IntersectionObserver(
        entries => {
          if (!entries.some(e => e.isIntersecting)) return;
          const s = stateRef.current;
          if (s.hasMore) {
            setShown(prev => Math.min(s.length, prev + s.step));
          }
        },
        { rootMargin }
      );
      observer.observe(el);
      observerRef.current = observer;
    },
    [rootMargin]
  );

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  // If IntersectionObserver is unavailable (SSR / old env), render everything.
  const canObserve = typeof IntersectionObserver !== 'undefined';
  const cap = canObserve ? shown : items.length;

  return {
    visible: items.slice(0, cap),
    sentinelRef,
    hasMore: canObserve ? hasMore : false,
    shownCount: Math.min(cap, items.length),
    total: items.length,
  };
}
